import * as Sentry from '@sentry/react';
import {
  MutationCache,
  QueryCache,
  QueryClient,
  type QueryClientConfig,
} from '@tanstack/react-query';
import axios from 'axios';

// Excludes queryCache/mutationCache so callers can't accidentally drop the Sentry hook.
type Overrides = Omit<QueryClientConfig, 'queryCache' | 'mutationCache'>;

// Stale URLs to deleted/unshared resources are routine; a 404 is the
// expected server answer, not an application error worth reporting.
function isNotFound (error: unknown): boolean {
  return axios.isAxiosError(error) && error.response?.status === 404;
}

// Client errors (4xx) are deterministic — retrying re-sends the same doomed
// request (react-query's default retry of 3 turned every stale watchlist
// URL into 4 requests per focus).
function retryExceptClientErrors (failureCount: number, error: unknown): boolean {
  if (axios.isAxiosError(error) && error.response && error.response.status < 500) {
    return false;
  }
  return failureCount < 3;
}

// Centralized QueryClient construction so every query/mutation failure is reported to Sentry.
// React Query v5 has no default console logger, so this is the only place that surfaces RQ errors.
export function createQueryClient (overrides: Overrides = {}): QueryClient {
  return new QueryClient({
    ...overrides,
    defaultOptions: {
      ...overrides.defaultOptions,
      queries: {
        retry: retryExceptClientErrors,
        ...overrides.defaultOptions?.queries,
      },
    },
    queryCache: new QueryCache({
      onError: (error, query) => {
        if (isNotFound(error)) return;
        Sentry.captureException(error, {
          tags: { rq_kind: 'query' },
          extra: { queryKey: query.queryKey, queryHash: query.queryHash },
        });
      },
    }),
    mutationCache: new MutationCache({
      onError: (error, _vars, _ctx, mutation) => {
        if (isNotFound(error)) return;
        Sentry.captureException(error, {
          tags: { rq_kind: 'mutation' },
          extra: { mutationKey: mutation.options.mutationKey },
        });
      },
    }),
  });
}
